import { AnalyticsProvider } from "@/Contexts/AnalyticsProvider";
import { CookieManagementProvider } from "@/Contexts/CookieManagementProvider";
import { ModalManagerProvider } from "@/Contexts/ModalManagerProvider";
import { listenToAppEvent, sendEventToApp } from "@/utils/appCommunication";
import { MobileAppTracking } from "@/utils/MobileAppTracking";
import { PropsWithChildren, useEffect } from "react";

/**
 * The providers every page needs, whichever entry point rendered it.
 *
 * Both SPAs mount this: the Inertia app wraps it in GlobalStateProviders and
 * adds the logged-in providers on top, while the marketing SPA (turboApp.tsx)
 * mounts it alone. The logged-in providers are deliberately not in here —
 * ChatProvider pulls in stream-chat-react and the payment/scheduling providers
 * pull in cronofy-elements, roughly 3MB of JavaScript that a logged-out
 * marketing page can never use but would still have to download before its
 * first paint.
 *
 * Components shared between the two still call useContext on the providers that
 * only exist in the app tree. That is safe — each of those contexts has a
 * default value, and the UI those defaults hide never renders for the
 * logged-out visitors the marketing SPA serves.
 */
export function RequiredProviders({ children }: PropsWithChildren) {
    useEffect(() => {
        sendEventToApp("appInitialized", {});
        const unsubscribe = listenToAppEvent("webViewAliveCheck", () => {
            sendEventToApp("isAlive", true);
        });
        return () => unsubscribe();
    }, []);

    return (
        <ModalManagerProvider>
            <CookieManagementProvider>
                <AnalyticsProvider>
                    <MobileAppTracking>{children}</MobileAppTracking>
                </AnalyticsProvider>
            </CookieManagementProvider>
        </ModalManagerProvider>
    );
}
